import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { AlertTriangle, Loader2, ArrowLeft } from "lucide-react";
import { resetPassword } from "@/lib/api";

const APP_NAME = import.meta.env.VITE_APP_NAME || "Hospital Management System";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [message, setMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) {
      setMessage("Invalid or missing reset link. Please request a new one.");
      setStatus("error");
      return;
    }
    if (newPassword.length < 6) {
      setMessage("Password must be at least 6 characters.");
      setStatus("error");
      return;
    }
    if (newPassword !== confirmPassword) {
      setMessage("Passwords do not match.");
      setStatus("error");
      return;
    }

    setStatus("loading");
    setMessage("");
    try {
      await resetPassword(token, newPassword);
      setStatus("success");
      setMessage("Your password has been reset. You can sign in with your new password.");
    } catch (err: any) {
      setStatus("error");
      setMessage(err?.message || "Reset failed. The link may have expired.");
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="max-w-md w-full text-center">
          <Alert variant="destructive" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>Invalid or missing reset link. Please use the link from your email or request a new one.</AlertDescription>
          </Alert>
          <Link to="/forgot-password" className="text-primary font-medium hover:underline">
            Request a new reset link
          </Link>
          <span className="mx-2 text-muted-foreground">|</span>
          <Link to="/login" className="text-primary font-medium hover:underline">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-background text-foreground">
      {/* Left: Branding */}
      <div className="hidden lg:flex lg:w-[44%] flex-col justify-between bg-gradient-to-br from-primary via-primary/95 to-primary/85 p-8 xl:p-12 text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '32px 32px' }} />
        <div className="relative z-10">
          <img src="/onecheckup-logo-primary.png" alt={APP_NAME} className="w-60 xl:w-72 object-contain brightness-0 invert mb-10" />
          <h2 className="text-2xl xl:text-3xl font-bold leading-tight max-w-md mb-3">
            Set a new password
          </h2>
          <p className="text-primary-foreground/80 text-sm max-w-sm leading-relaxed">
            Choose a strong password you don't use elsewhere. We recommend at least 6 characters.
          </p>
        </div>
        <p className="text-[10px] text-primary-foreground/70 relative z-10">Your clinic data is encrypted and securely stored.</p>
      </div>

      {/* Right: Form */}
      <div className="flex-1 flex flex-col justify-center items-center px-4 py-6 sm:px-6 sm:py-8 lg:px-8 lg:py-10 bg-background">
        <div className="w-full max-w-[380px]">
          <div className="lg:hidden flex items-center justify-center mb-6">
            <img src="/onecheckup-logo-primary.png" alt={APP_NAME} className="w-56 object-contain" />
          </div>

          <Link
            to="/login"
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to sign in
          </Link>

          <h1 className="text-xl font-semibold text-foreground mb-0.5">Set new password</h1>
          <p className="text-muted-foreground text-sm mb-5">
            Enter your new password below.
          </p>

          {status === "success" ? (
            <>
              <Alert className="mb-4 border-border bg-success/10 text-foreground">
                <AlertDescription>{message}</AlertDescription>
              </Alert>
              <Button asChild className="w-full h-10 rounded-lg font-medium text-sm">
                <Link to="/login">Sign in</Link>
              </Button>
            </>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="newPassword" className="text-foreground text-sm">New password</Label>
                <Input
                  id="newPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="At least 6 characters"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={status === "loading"}
                  minLength={6}
                  required
                  autoComplete="new-password"
                  className="h-10 rounded-lg border-input bg-background text-sm"
                />
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <Label htmlFor="confirmPassword" className="text-foreground text-sm">Confirm password</Label>
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-xs text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? "Hide" : "Show"}
                  </button>
                </div>
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={status === "loading"}
                  minLength={6}
                  required
                  autoComplete="new-password"
                  className="h-10 rounded-lg border-input bg-background text-sm"
                />
              </div>

              {status === "error" && message && (
                <Alert variant="destructive" className="text-sm">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>{message}</AlertDescription>
                </Alert>
              )}

              <Button type="submit" disabled={status === "loading"} className="w-full h-10 rounded-lg font-medium text-sm">
                {status === "loading" ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Resetting...
                  </>
                ) : (
                  "Reset password"
                )}
              </Button>
            </form>
          )}

          <p className="mt-4 text-xs text-center text-muted-foreground">
            <Link to="/login" className="text-primary font-medium hover:underline">
              Back to sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
